import Modal from "../common/Modal";
import UserIcon from "../common/UserIcon";
import Store from "../../utils/Store";
import { useStoreState } from "pullstate";
import { useContext, useEffect, useState } from "react";
import { getTrimmedWorkspaceId, hasSiteKeyword } from "../../utils";
import updateWorkspaceReferral from "./services/updateWorkspaceReferral";
import Toast from "../Toast";
import { t } from "i18next";
import { PatientsContext } from "../Patients/PatientsContext";

interface Referral {
  referral_id: number;
  workspace_id: number;
  workspace_name: string;
  source_uuid: string;
  requester_name: string;
  requester_email: string;
  status: number;
  updated_by: string | null;
  updated_at: string;
}

const AudibleNotifications = () => {
  const [pendingReferrals, setPendingReferrals] = useState<Referral[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingStates, setLoadingStates] = useState<boolean>(false);
  const { pendingReferral } = useContext(PatientsContext);
  const _hasSiteKeyword = hasSiteKeyword();
  const { workspaceAudibleNotifications } = useStoreState(Store, (s) => ({
    workspaceAudibleNotifications: s.workspaceAudibleNotifications,
  }));

  useEffect(() => {
    if (pendingReferral) {
      setPendingReferrals(pendingReferral?.filter((ws: any) => ws.status === 0));
      setCurrentIndex(0);
      Store.update((s) => {
        s.workspaceAudibleNotifications = true;
      });
    }
  }, [pendingReferral]);

  const handleAction = async (action: "approve" | "reject" | "decide_later") => {
    const currentReferral = pendingReferrals[currentIndex];

    if (!currentReferral) {
      Toast.error(t("no_current_referral_found"));
      return;
    }

    setLoadingStates(true);

    try {
      await updateWorkspaceReferral(currentReferral.workspace_id, {
        referral_id: currentReferral.referral_id,
        status: action === "approve" ? 2 : action === "reject" ? 3 : 1,
      });

      // Remove the processed referral from the list
      const updatedReferrals = pendingReferrals.filter(
        (_, index) => index !== currentIndex
      );
      setPendingReferrals(updatedReferrals);

      // Check if there are more referrals to process
      if (updatedReferrals.length > 0) {
        // If we were at the last item, go to the new last item
        if (currentIndex >= updatedReferrals.length) {
          setCurrentIndex(updatedReferrals.length - 1);
        }
        // If we were not at the last item, stay at the same index
        // (since the item at currentIndex was removed, the next item is now at currentIndex)
      } else {
        // All referrals processed, close modal
        Store.update((s) => {
          s.workspaceAudibleNotifications = false;
        });
        setCurrentIndex(0);
      }
    } catch (error) {
      Toast.error(t("something_went_wrong"));
    } finally {
      setLoadingStates(false);
    }
  };

  const handleDecideLater = () => {
    if (!loadingStates) {
      handleAction("decide_later");
    }
  };

  const handleApprove = () => {
    if (!loadingStates) {
      handleAction("approve");
    }
  };

  const handleReject = () => {
    if (!loadingStates) {
      handleAction("reject");
    }
  };

  const currentReferral = pendingReferrals[currentIndex];

  if (!currentReferral) {
    return null;
  }

  return (
    <Modal
      closeOnClickAway={!loadingStates}
      show={workspaceAudibleNotifications}
      className="w-full max-w-sm !rounded-xl overflow-hidden !text-left mb-8 !bg-gray-900 dark:!bg-gray-100 !text-gray-900 border border-gray-700 dark:border-gray-300"
    >
      <div className="flex justify-between items-center px-5 py-4 text-gray-100 dark:text-gray-900 bg-gray-800 dark:bg-gray-200 border-b border-gray-700 dark:border-gray-300">
        <p>
          {pendingReferrals?.length}{" "}
          {t(_hasSiteKeyword ? "new_site_request" : "new_workspace_request")}
          {pendingReferrals?.length > 1 ? "s" : ""}
        </p>
        <p>
          {currentIndex + 1}/{pendingReferrals?.length}
        </p>
      </div>

      <div className="flex flex-col">
        <div className="p-5 border-b border-gray-700 dark:border-gray-300">
          <p className="font-medium text-gray-100 dark:text-gray-800">
            {currentReferral.workspace_name}
          </p>
          <p className="text-gray-300 dark:text-gray-700 text-md">
            #{getTrimmedWorkspaceId(currentReferral?.source_uuid)}
            {/* | 14 members | 22 patients */}
          </p>
          {/* <p className="text-gray-600 text-sm">
            {t("requested")}: {new Date(currentReferral.updated_at).toLocaleDateString()}
          </p> */}
        </div>
        <div className="p-5 space-y-4">
          <div className="flex space-x-4 items-center">
            <UserIcon
              name={currentReferral.requester_name}
              className="!bg-gray-800 dark:!bg-gray-200 !text-gray-200 dark:!text-gray-800 p-3 !rounded-full !w-[40px] !h-[40px] font-medium"
            />
            <div>
              <p className="text-gray-200 dark:text-gray-800 font-medium">
                {currentReferral.requester_email}
              </p>
              <p className="text-gray-600 text-md">
                {t(
                  _hasSiteKeyword
                    ? "requesting_access_to_site"
                    : "requesting_access_to_workspace"
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between font-medium space-x-4">
            <button
              disabled={loadingStates}
              className={`text-teal-400 dark:text-teal-500 hover:text-teal-600 transition-colors outline-none ${
                loadingStates ? "opacity-50 cursor-not-allowed" : ""
              }`}
              onClick={handleDecideLater}
            >
              {t("button.decide_later")}
            </button>
            <div className="flex space-x-4 items-center">
              <button
                disabled={loadingStates}
                onClick={handleReject}
                className={`p-5 bg-red-600 text-gray-100 rounded-lg hover:bg-red-700 transition-colors ${
                  loadingStates ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {t("button.reject")}
              </button>
              <button
                disabled={loadingStates}
                onClick={handleApprove}
                className={`p-5 bg-green-600 text-gray-100 rounded-lg hover:bg-green-700 transition-colors ${
                  loadingStates ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {t("button.approve")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AudibleNotifications;
